import { composeWithUi, isScopable, LayoutProps } from "@jsonforms/core";
import { withJsonFormsLayoutProps } from "@jsonforms/react";
import { ComponentType, createContext, useContext, useMemo } from "react";

import { DebugWrapper } from "./DebugWrapper";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";

export interface CascadiaLayoutProps extends LayoutProps {
  touched: boolean;
  invalid: boolean;
  description?: string;
  errors: string[];
  stopProcessingErrors?: boolean;
  setTouched(): void;
  options: Record<string, any>;
}

export const LayoutContext = createContext<CascadiaLayoutProps>({
  touched: true,
  invalid: false,
  errors: [],
  setTouched(): void {},
  options: {},
  direction: "column",
  uischema: { type: "" },
  schema: {},
  enabled: true,
  visible: true,
  path: "",
});

export function withCascadiaLayoutProps(
  Component: ComponentType<CascadiaLayoutProps>
) {
  return withJsonFormsLayoutProps((props: LayoutProps) => {
    const parentLayout = useLayoutProps();

    const form = useContext(CascadiaJsonFormContext);

    const path = useMemo(() => {
      if (props.path) {
        return props.path;
      }

      if (isScopable(props.uischema)) {
        return composeWithUi(props.uischema, props.path);
      }
    }, [props.path, props.uischema]);

    const errors = useMemo(() => {
      if (!path) {
        return [];
      }

      let errors: string[] = [];

      const submitErrors = form.submitErrors[props.path];

      if (Array.isArray(submitErrors)) {
        errors = errors.concat(submitErrors);
      } else if (typeof submitErrors === "string") {
        errors.push(submitErrors);
      }

      return errors;
    }, [path, form.submitErrors[props.path]]);

    const touched = useMemo(
      () =>
        form.touched
          ? Boolean((path && form.touched[path]) || form.isSubmitted)
          : true, // If we aren't tracking touched state, mark as touched
      [path, form.touched, form.isSubmitted]
    );

    const cascadiaProps = useMemo(
      () => ({
        touched,
        invalid: touched && errors.length > 0,
        options: props.uischema.options || {},
        description: props.uischema.options?.description,
        errors,
        setTouched() {
          if (path) {
            form.setTouched(path);
          }

          parentLayout.setTouched();
        },
      }),
      [path, touched, form.setTouched, errors]
    );

    // Use display: none to hide the control, but still render it
    const hidden = props.uischema.options?.hidden === true;

    // don't render the control if the "hide" rule was triggered
    if (parentLayout.visible === false || props.visible === false) {
      return null;
    }

    return (
      <LayoutContext.Provider
        value={{
          ...props,
          ...cascadiaProps,
          stopProcessingErrors:
            parentLayout.stopProcessingErrors || errors.length > 0,
        }}
      >
        <DebugWrapper
          Component={Component}
          componentProps={{ ...props, ...cascadiaProps }}
          type="layout"
          hidden={hidden}
        />
      </LayoutContext.Provider>
    );
  });
}

export function useLayoutProps() {
  return useContext(LayoutContext);
}
